import { mapValues } from 'lodash/fp';

import yup from '#/libs/yup';
import { ContainerPacking, VehicleStandalone } from '#/__generated__/graphql/graphql';
import { MANAGE_CUSTOMER, MANAGE_LOGISTIC } from './manage-user-type-list';
import { calcMaxCargoWeightInKg } from '#/utils/cargo-weight';

export const contactPointSchema = {
  // contactType: yup.string().required(),
  email: yup.string().email(),
  // .when('telephone', {
  //   is: (value: string) => !value,
  //   then: (schema) => schema.required(),
  // }),
  name: yup.string().label('Name'),
  telephone: yup.string().label('Contact Number').phoneNumber(),
  // .when('email', {
  //   is: (value: string) => !value,
  //   then: (schema) => schema.required(),
  // }),
};

export const offerSchema = {
  inventoryLevel: yup.number().label('Truck Availability').required(),
  itemOfferedId: yup.string().label('Truck Type').required(),
};

export const postalAddressSchema = {
  addressCountry: yup.string().label('Country').length(2),
  addressLocality: yup.string().label('City'),
  addressRegion: yup.string().label('State'),
  postalCode: yup
    .string()
    .label('Postal Code')
    .matches(/^\d{5}$/, 'Postal Code should be exactly 5 characters length.'),
  streetAddress: yup.string().label('Street Address'),
};

export const organizationSchema = {
  address: yup
    .object(mapValues((schema: yup.AnySchema) => schema.required())(postalAddressSchema))
    .required(),
  areasServed: yup.array(yup.string()).label('Frequent Locations').min(1).required(),
  companyNumber: yup.string().label('Company Registration Number').required(),
  contactPoints: yup
    .array(yup.object(contactPointSchema))
    .label('Person In Charge')
    .min(1, 'At least One (1) Person In Charge is required')
    .required(),
  description: yup.string(),
  name: yup.string().label('Company Name').required(),
  referral: yup.string(),
  telephone: yup.string().label('Office Number').phoneNumber().required(),
};

export const customerSchema = {
  ...organizationSchema,
  businessCategoryId: yup.string().label('Business Type').required(),
  cargoDetails: yup.string().label('Cargo Details').required(),
  otherBusinessCategory: yup.string().when('businessCategoryId', {
    is: 'other',
    then: (schema) => schema.required(),
    otherwise: (schema) => schema.strip(),
  }),
};

export const authenticateSchema = {
  username: yup.string().label('Username').required(),
  password: yup.string().label('Password').required(),
};

export const createUserSchema = {
  email: yup.string().email().label('Email').required(),
  username: yup.string().label('Username').required(),
  password: yup.string().label('Password').required(),
  manageUserCode: yup.string().label('Manage User Type').required(),
  customerId: yup
    .string()
    .label('Manage Customer')
    .when('manageUserCode', {
      is: MANAGE_CUSTOMER.code,
      then: (schema) => schema.required(),
    }),
  logisticsId: yup
    .string()
    .label('Manage Logistic')
    .when('manageUserCode', {
      is: MANAGE_LOGISTIC.code,
      then: (schema) => schema.required(),
    }),
};

export const forgotPasswordSchema = yup.object({
  username: yup.string().label('Username').required(),
});

export const logisticsSchema = {
  ...organizationSchema,
  offers: yup
    .array(yup.object(offerSchema))
    .label('Truck Types & Availabilities')
    .min(1)
    .required(),
};

export const freightDeliverySchema = {
  driver: yup.string().label('Driver').required(),
  driverTelephone: yup.string().label('Contact Number').phoneNumber().required(),
  // expectedArrivalDate: yup.date().label('Delivery Date').required(),
  // vehicleBodyType: yup.string().label('Vehicle Type').required(),
  vehicleRegistrationPlate: yup.string().label('Vehicle Registration Number').required(),
};

export const orderCancellationSchema = {
  remark: yup.string().when('selection', {
    is: '99',
    then: (schema) => schema.required('Please input a reason.'),
  }),
  selection: yup.string().required('Please select a reason.'),
};

export const resetPasswordSchema = {
  password: yup.string().label('Password').required(),
  confirmPassword: yup
    .string()
    .label('Confirm password')
    .oneOf([yup.ref('password'), null], 'Confirm Password not matched.')
    .required(),
};

export const rfqSchema = yup.object().shape(
  {
    deliveryTime: yup
      .date()
      .label('Delivery Time')
      .when(
        'pickupTime',
        (pickupTime, schema) =>
          pickupTime && schema.min(pickupTime, 'Delivery Time cannot be earlier than Pickup Time'),
      )
      .required(),
    pickupTime: yup
      .date()
      .label('Pickup Time')
      .when(
        'deliveryTime',
        (deliveryTime, schema) =>
          deliveryTime &&
          schema.max(deliveryTime, 'Pickup Time cannot be later than Delivery Time'),
      )
      .required(),
    cargoDetails: yup
      .object({
        cargoDimensions: yup.object({
          height: yup.object({
            value: yup
              .number()
              .positive()
              .label('Height')
              .required()
              .typeError('Height must be a positive number.'),
          }),
          length: yup.object({
            value: yup
              .number()
              .positive()
              .label('Length')
              .required()
              .typeError('Length must be a positive number.'),
          }),
          width: yup.object({
            value: yup
              .number()
              .positive()
              .label('Width')
              .required()
              .typeError('Width must be a positive number.'),
          }),
        }),
        cargoWeight: yup.object({
          value: yup
            .number()
            .positive()
            .label('Weight')
            .required()
            .typeError('Weight must be a positive number.'),
        }),
        description: yup.string().label('Description').required(),
      })
      .when('preferredVehicle', (preferredVehicle: VehicleStandalone, schema) => {
        if (!!preferredVehicle.cargoVolume?.unitCode) {
          return schema.shape({
            cargoWeight: yup.object({
              value: yup
                .number()
                .positive()
                .label('Weight')
                .max(calcMaxCargoWeightInKg(preferredVehicle.cargoVolume)),
            }),
          });
        }

        return schema;
      }),
    preferredVehicle: yup
      .object()
      .label('Truck Size')
      .when('preferredContainerPacking', {
        is: ContainerPacking.LessThanContainerLoad,
        then: (schema) => schema.strip(),
        otherwise: (schema) => schema.required(),
      }),
    routeLegs: yup
      .array(
        yup.object({
          endContactPoint: yup
            .object({
              ...contactPointSchema,
              name: contactPointSchema.name.required(),
              telephone: contactPointSchema.telephone.required(),
            })
            .label('Contact Person')
            .required(),
          endLocation: yup.object({
            address: yup
              .object(mapValues((schema: yup.AnySchema) => schema.required())(postalAddressSchema))
              .label('Destination address')
              .required(),
          }),
          startContactPoint: yup
            .object({
              ...contactPointSchema,
              name: contactPointSchema.name.required(),
              telephone: contactPointSchema.telephone.required(),
            })
            .label('Contact Person')
            .required(),
          startLocation: yup.object({
            address: yup
              .object(mapValues((schema: yup.AnySchema) => schema.required())(postalAddressSchema))
              .label('Pickup address')
              .required(),
          }),
        }),
      )
      .required(),
  },
  [['deliveryTime', 'pickupTime']],
);
